import { useEffect, useState } from "react";
import axios from "axios";
import { formatEuro } from "@/data/pricing";
import { Loader2, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS = {
  paid: { label: "Payée", cls: "bg-[#6EB000]/15 text-[#6EB000] border-[#6EB000]/40" },
  pending_payment: { label: "En attente de paiement", cls: "bg-[#F5A623]/10 text-[#F5A623] border-[#F5A623]/40" },
  quote_requested: { label: "Devis à confirmer", cls: "bg-white/5 text-white/70 border-white/20" },
  payment_failed: { label: "Paiement échoué", cls: "bg-red-500/10 text-red-400 border-red-500/40" },
  pay_on_site: { label: "Paiement sur place – à encaisser", cls: "bg-white/5 text-white/70 border-white/20" },
};

export default function Admin() {
  const [key, setKey] = useState(localStorage.getItem("nbperf_admin_key") || "");
  const [orders, setOrders] = useState(null);
  const [bookings, setBookings] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async (k) => {
    setLoading(true);
    try {
      const [o, b] = await Promise.all([
        axios.get(`${API}/orders`, { headers: { "X-Admin-Key": k } }),
        axios.get(`${API}/bookings`, { headers: { "X-Admin-Key": k } }),
      ]);
      setOrders(o.data);
      setBookings(b.data);
      setError("");
      localStorage.setItem("nbperf_admin_key", k);
    } catch {
      setError("Clé invalide ou accès refusé.");
      setOrders(null);
      setBookings(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Admin — NBPerf";
    if (key) load(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main className="pt-16 min-h-screen" data-testid="admin-page">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16">
        <h1 className="font-display text-4xl sm:text-6xl text-white">Espace <span className="text-[#6EB000]">admin</span></h1>
        <p className="text-white/50 mt-3 text-sm">Accès privé NBPerf — commandes, paiements, factures et rendez-vous.</p>

        <div className="mt-8 flex flex-wrap gap-3">
          <input
            type="password"
            placeholder="Clé admin"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            className="bg-transparent border border-[#454C50] focus:border-[#6EB000] outline-none px-4 py-3 text-sm text-white w-64"
            data-testid="admin-key-input"
          />
          <button
            onClick={() => load(key)}
            className="clip-angle inline-flex items-center gap-2 bg-[#6EB000] text-black font-bold px-6 py-3 text-sm uppercase tracking-widest hover:bg-[#5A9000]"
            data-testid="admin-load-button"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Charger
          </button>
        </div>
        {error && <p className="text-red-400 text-sm mt-4" data-testid="admin-error">{error}</p>}

        {bookings && (
          <>
            <h2 className="font-display text-2xl sm:text-3xl text-white mt-14 mb-6">Rendez-vous</h2>
            <div className="space-y-4" data-testid="admin-bookings-list">
              {bookings.length === 0 && <p className="text-white/40">Aucune réservation pour le moment.</p>}
              {bookings.map((b) => (
                <div key={b.booking_id} className="border border-[#454C50] bg-[#343A3D] p-5 sm:p-6" data-testid={`admin-booking-${b.booking_id.slice(0, 8)}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <span className="text-[11px] uppercase tracking-widest border px-3 py-1 bg-[#6EB000]/15 text-[#6EB000] border-[#6EB000]/40">
                      {new Date(b.date + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" })} · {b.time}
                    </span>
                    <span className="text-white/40 text-xs">{b.prestation}</span>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-4 mt-4 text-sm">
                    <div>
                      <p className="text-[11px] uppercase tracking-widest text-white/40 mb-1">Client</p>
                      <p className="text-white">{b.nom}</p>
                      <p className="text-white/50">{b.telephone}</p>
                      <p className="text-white/50 break-all">{b.email}</p>
                    </div>
                    <div>
                      <p className="text-[11px] uppercase tracking-widest text-white/40 mb-1">Adresse</p>
                      <p className="text-white/70">{b.adresse}</p>
                      <p className="text-white/70">{b.code_postal} {b.ville}</p>
                    </div>
                    <div>
                      <p className="text-[11px] uppercase tracking-widest text-white/40 mb-1">Véhicule</p>
                      <p className="text-white/70">{b.vehicule}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {orders && (
          <>
            <h2 className="font-display text-2xl sm:text-3xl text-white mt-14 mb-6">Commandes &amp; paiements</h2>
            <div className="space-y-4" data-testid="admin-orders-list">
              {orders.length === 0 && <p className="text-white/40">Aucune commande pour le moment.</p>}
              {orders.map((o) => {
                const st = STATUS[o.status] || STATUS.pending_payment;
                return (
                  <div key={o.order_id} className="border border-[#454C50] bg-[#343A3D] p-5 sm:p-6" data-testid={`admin-order-${o.order_id.slice(0, 8)}`}>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <span className={`text-[11px] uppercase tracking-widest border px-3 py-1 ${st.cls}`}>{st.label}</span>
                        <span className="text-white/40 text-xs">{new Date(o.created_at).toLocaleString("fr-FR")}</span>
                        {o.invoice_number && (
                          <span className="text-[11px] uppercase tracking-widest border px-3 py-1 bg-white/5 text-white/70 border-white/20" data-testid="admin-invoice-badge">
                            Facture {o.invoice_number}
                          </span>
                        )}
                      </div>
                      {o.amount_eur != null && <span className="font-display text-2xl text-[#6EB000]">{formatEuro(o.amount_eur)}</span>}
                    </div>
                    <div className="grid sm:grid-cols-3 gap-4 mt-4 text-sm">
                      <div>
                        <p className="text-[11px] uppercase tracking-widest text-white/40 mb-1">Client</p>
                        <p className="text-white">{o.customer.prenom} {o.customer.nom}</p>
                        <p className="text-white/50">{o.customer.telephone}</p>
                        <p className="text-white/50 break-all">{o.customer.email}</p>
                        {o.customer.adresse && (
                          <p className="text-white/50">{o.customer.adresse}, {o.customer.code_postal} {o.customer.ville}</p>
                        )}
                      </div>
                      <div>
                        <p className="text-[11px] uppercase tracking-widest text-white/40 mb-1">Véhicule</p>
                        <p className="text-white">{o.vehicle.marque} {o.vehicle.modele} ({o.vehicle.annee})</p>
                        <p className="text-white/50">{o.vehicle.motorisation} · {o.vehicle.carburant} · {o.vehicle.puissance}</p>
                        {o.vehicle.immatriculation && <p className="text-white/50">Immat : {o.vehicle.immatriculation}</p>}
                      </div>
                      <div>
                        <p className="text-[11px] uppercase tracking-widest text-white/40 mb-1">Prestation</p>
                        <p className="text-white">{o.prestation.label} — {o.prestation.price_label}</p>
                        {o.options.map((x) => (
                          <p key={x.id} className="text-white/50">+ {x.label} — {x.price_label}</p>
                        ))}
                      </div>
                    </div>
                    <p className="text-[11px] text-white/30 mt-4 font-mono">
                      Réf. {o.order_id}
                      {o.payment?.ref && <> · Paiement {o.payment.method} : {o.payment.ref}</>}
                    </p>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </main>
  );
}
